/**
 * Intro Request model - explicit state definition.
 * 
 * Elm-ish architecture: State is explicit and typed.
 * No implicit state machines encoded in useState hooks.
 * 
 * Safety doctrine:
 * - No direct contact details collected
 * - No legal name, phone, email, address
 * - No employer, income, net worth
 * - No social media handles
 * - Optional short message only
 * - Required acknowledgement checkbox
 */
import type { FriendProfile } from '@/api/profiles';
import { MESSAGE_MAX_LENGTH } from '@/api/introRequests';

/**
 * Validation error types for intro request form.
 */
export type IntroRequestValidationError =
  | 'message_too_long'
  | 'acknowledgement_required';

/**
 * Status values for the intro request program lifecycle.
 */
export type IntroRequestStatus =
  | 'idle'        // Initial state before any action
  | 'loading'     // Loading card data and checking duplicate
  | 'no_card'     // No cardId provided or card not found
  | 'duplicate'   // Intro request already exists for this card
  | 'form'        // Showing the submission form
  | 'submitting'  // Submitting the intro request
  | 'success'     // Successfully submitted
  | 'error';      // An error occurred

/**
 * Card data to display in the form (identity-minimized).
 * Only displays safe, curated information.
 */
export interface IntroRequestCard {
  readonly id: string;
  readonly displayTitle: string;
}

/**
 * Complete state shape for the Intro Request program.
 * 
 * Security posture:
 * - All state is local to this program instance
 * - Card data is identity-minimized (no personal details)
 * - Error messages are user-safe (no internal details exposed)
 * - Audit events are TODO-annotated (pending ACT 7)
 */
export interface IntroRequestModel {
  /** Current status of the program */
  readonly status: IntroRequestStatus;
  /** Card ID being requested (from URL param) */
  readonly cardId: string | null;
  /** Card data (identity-minimized) */
  readonly card: IntroRequestCard | null;
  /** Message text from the form */
  readonly message: string;
  /** Whether acknowledgement checkbox is checked */
  readonly acknowledgementChecked: boolean;
  /** Validation errors for the form */
  readonly validationErrors: readonly IntroRequestValidationError[];
  /** Error message when status is 'error' */
  readonly error: string | null;
  /** Duplicate request ID if duplicate detected */
  readonly duplicateRequestId: string | null;
  /** Submit result request ID on success */
  readonly requestId: string | null;
}

/**
 * Initial/empty state for the intro request program.
 */
export const initialModel: IntroRequestModel = {
  status: 'idle',
  cardId: null,
  card: null,
  message: '',
  acknowledgementChecked: false,
  validationErrors: [],
  error: null,
  duplicateRequestId: null,
  requestId: null,
};

/**
 * Maximum message length per safety doctrine.
 */
export const MAX_MESSAGE_LENGTH = MESSAGE_MAX_LENGTH; // 280

/**
 * Helper to validate the form.
 * Returns array of validation errors (empty if valid).
 */
export function validateForm(
  message: string,
  acknowledgementChecked: boolean
): readonly IntroRequestValidationError[] {
  const errors: IntroRequestValidationError[] = [];
  
  if (message.length > MAX_MESSAGE_LENGTH) {
    errors.push('message_too_long');
  }
  
  if (!acknowledgementChecked) {
    errors.push('acknowledgement_required');
  }
  
  return errors;
}

/**
 * Helper to check if form is valid for submission.
 */
export function isFormValid(model: IntroRequestModel): boolean {
  return model.acknowledgementChecked && model.message.length <= MAX_MESSAGE_LENGTH;
}

/**
 * Helper to check if the program is in a terminal state.
 */
export function isTerminalState(model: IntroRequestModel): boolean {
  return model.status === 'success' || model.status === 'no_card' || model.status === 'duplicate';
}

/**
 * Helper to check if we're in a state where the form is shown.
 */
export function isFormState(model: IntroRequestModel): boolean {
  return model.status === 'form';
}

/**
 * Convert API profile to intro request card (identity-minimized).
 * Maps backend FriendProfile to the display format needed for intro request.
 */
export function toIntroRequestCard(profile: FriendProfile): IntroRequestCard {
  return {
    id: profile.id,
    displayTitle: profile.display_name || 'Anonymous Friend',
  };
}
